/**
 * Auth API client.
 *
 * All session-authenticated requests send the httpOnly `zt_token` cookie rather
 * than reading any token from JavaScript-accessible storage — see httpClient.ts,
 * which owns that plumbing and the `{ error }` failure convention.
 */
import { postJson, deleteJson } from './httpClient';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

/** Current session user returned by GET /api/auth/me */
export interface MeResponse {
    id: string;
    name: string;
    plan: string;
    linkedinId: string;
    googleId: string;
    email: string;
    githubConnected?: boolean;
    githubTokenUpdatedAt?: string | null;
    error?: string;
}

/**
 * Validates the current session cookie and returns the authenticated user.
 * Returns null when no valid session exists (401).
 *
 * Kept on raw fetch: the null contract is driven by the response *status*, which
 * httpClient deliberately hides — it reports the API's own error body instead.
 */
export async function getMe(): Promise<MeResponse | null> {
    const resp = await fetch(`${API_BASE}/api/auth/me`, {
        credentials: 'include',
    });
    if (resp.status === 401 || resp.status === 404) return null;
    return resp.json();
}

/** Disconnect GitHub OAuth for current user (removes stored token). */
export async function disconnectGitHub(): Promise<{ success?: boolean; error?: string }> {
    return postJson<{ success?: boolean }>('/api/auth/github/disconnect');
}

/** Clears the session cookie server-side. */
export async function logoutUser(): Promise<void> {
    await postJson('/api/auth/logout');
}

/** Requests permanent deletion of the authenticated user's account and all data. */
export async function deleteAccount(): Promise<{ success?: boolean; error?: string }> {
    return deleteJson<{ success?: boolean }>('/api/user');
}

/**
 * Downloads all personal data as a JSON blob (GDPR Art. 20).
 *
 * Kept on raw fetch: the response is consumed as a Blob for a file download, not
 * parsed as JSON.
 */
export async function exportData(): Promise<void> {
    const resp = await fetch(`${API_BASE}/api/user/export`, {
        credentials: 'include',
    });
    if (!resp.ok) throw new Error('Export failed');
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'my-zeytune-data.json';
    a.click();
    URL.revokeObjectURL(url);
}
